/**
 * `useAttention(prefix)`: the roll-up of every attention source at or below
 * `prefix`, on whole path segments (spec §6.5). `admin/profiles` includes
 * `admin/profiles/access/pending` but not `admin/profiles-x/y`.
 *
 * Unknown (never fetched) and disabled sources contribute nothing; a source
 * in `error` keeps contributing its last good count.
 *
 * Both selections go through `useShallow`, and the counts selection holds
 * only this prefix's non-zero counts, so a refresh that changes an unrelated
 * source — or lands the same count again — does not re-render the caller.
 */
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAttentionStore } from './attentionStore';
import { pathHasPrefix } from './paths';
import type { AttentionItem, AttentionSource, AttentionSummary } from './types';

const SEVERITY_ORDER = { danger: 0, warn: 1 } as const;

export function useAttention(prefix: string): AttentionSummary {
  const sources = useAttentionStore(
    useShallow((s) => Object.values(s.sources).filter((src) => pathHasPrefix(src.path, prefix))),
  );
  const counts = useAttentionStore(
    useShallow((s) => {
      const out: Record<string, number> = {};
      for (const src of Object.values(s.sources)) {
        if (!pathHasPrefix(src.path, prefix)) continue;
        const st = s.states[src.id];
        if (st && st.status !== 'disabled' && st.count !== undefined && st.count > 0) {
          out[src.id] = st.count;
        }
      }
      return out;
    }),
  );
  return useMemo(() => summarize(sources, counts), [sources, counts]);
}

function summarize(sources: AttentionSource[], counts: Record<string, number>): AttentionSummary {
  const bySeverity = { warn: 0, danger: 0 };
  const items: AttentionItem[] = [];
  for (const src of sources) {
    const count = counts[src.id];
    if (count === undefined) continue;
    bySeverity[src.severity] += count;
    items.push({ id: src.id, severity: src.severity, count, label: src.label(count) });
  }
  items.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  const total = bySeverity.warn + bySeverity.danger;
  let worst: AttentionSummary['worst'];
  if (bySeverity.danger > 0) worst = 'danger';
  else if (bySeverity.warn > 0) worst = 'warn';
  return { total, worst, bySeverity, items };
}
